import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

import {
  createStream,
  deleteStream,
  getStream,
  getStreams,
  updateStream,
} from '../../../../../shared/api/magellan/stream';
import { searchTendersPreview } from '../../../../../shared/api/magellan/tender';
import type Stream from '../../../../../shared/entities/Stream';
import type { Filters } from '../../../../../shared/entities/StreamFilterSettings';

export function useCreateStreamMutation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: createStream,
    onSuccess: createdStream => {
      queryClient.invalidateQueries({ queryKey: [getStreams.name] });
      navigate(`/flux/${createdStream.id}`);
    },
  });
}

export function useStreamsQuery() {
  return useSuspenseQuery({
    queryKey: [getStreams.name],
    queryFn: getStreams,
  });
}

export function useSearchTendersPreviewQuery(filterSettings: Filters) {
  return useQuery({
    queryKey: [searchTendersPreview.name, filterSettings],
    queryFn: () => searchTendersPreview(filterSettings),
    enabled: !!filterSettings.status.length,
    placeholderData: keepPreviousData,
  });
}

export function useStreamQuery(streamId: number | undefined) {
  return useQuery<Stream, AxiosError>({
    queryKey: [getStream.name, streamId],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getStream(streamId!),
    enabled: !!streamId,
  });
}

export function useDeleteStreamMutation() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: deleteStream,
    onSuccess: () => {
      navigate('/');
    },
  });
}

export function useUpdateStreamMutation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: updateStream,
    onSuccess: updatedStream => {
      queryClient.invalidateQueries({ queryKey: [getStreams.name] });
      navigate(`/flux/${updatedStream.id}`);
    },
  });
}
